/**
 * Serializing a sketch to and from a persistable document.
 *
 * This is the pure, DOM- and IndexedDB-free half of persistence: it turns the
 * in-memory sketch state (plus its identity metadata) into a plain JSON-safe
 * document and back. The actual IndexedDB read/write lives in local-db.js.
 * Keeping the transform pure means the round-trip is unit-tested without a
 * browser, and the storage backend can change (or a Supabase sync layer be
 * added) without touching this logic.
 *
 * IDENTITY. Each sketch is identified by Client / Opportunity / Space — the
 * shape that ties a sketch back to a Salesforce opportunity while capturing it
 * at the space level (project owner, 2026-07-23). These map onto the schema
 * later: opportunity → sketch.projects.salesforce_opportunity_id, space →
 * sketch.rooms. The metadata is optional at the storage layer so an untitled
 * work-in-progress still saves.
 *
 * Offline-first (ARCHITECTURE.md): the local store is the source of truth the
 * UI reads and writes; a future sync engine reconciles it with Supabase. Only
 * DURABLE state is persisted — raw strokes and the in-progress stroke are
 * transient scaffolding, and the closure result / pxPerUnit are derived, so all
 * are recomputed on load, never stored.
 */

import { ensureInteriorElementIds, normalizeDoors, normalizeInterior } from '../geometry/interior.js';

/** Bump when the document shape changes incompatibly. */
export const SKETCH_SCHEMA_VERSION = 1;

/**
 * @typedef {object} SketchMeta
 * @property {string} id
 * @property {string} client
 * @property {string} opportunity
 * @property {string} space
 * @property {string} [version]      legacy wire field; normal spaces store "Version 1"
 * @property {boolean} [isPreferred] legacy wire field; normal spaces store false
 * @property {string} [updatedAt]
 */

const clean = (value) => String(value ?? '').trim();
const cleanMeasurementState = (value) => (
  value === 'inherited' || value === 'measured' ? value : null
);

function cleanConnectingShelf(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    top: source.top !== false,
    bottom: source.bottom !== false,
    depthUnits: Number.isFinite(source.depthUnits) && source.depthUnits > 0 ? source.depthUnits : null,
  };
}

/** Wall-attached opening details. Kept deliberately descriptive: no fit logic. */
function cleanOpening(value) {
  if (!value || typeof value !== 'object') return null;
  const kinds = new Set(['swing', 'pocket', 'bifold_single', 'bifold_double', 'bypass', 'cased_opening', 'uncased_opening']);
  if (!kinds.has(value.kind)) return null;
  const out = { kind: value.kind, trim: value.trim === 'uncased' ? 'uncased' : 'cased' };
  for (const key of ['clearOpeningWidthUnits', 'clearOpeningHeightUnits']) {
    if (Number.isFinite(value[key]) && value[key] > 0) out[key] = value[key];
  }
  if (value.kind === 'swing') {
    out.swingDirection = value.swingDirection === 'out' ? 'out' : 'in';
    out.hingeSide = value.hingeSide === 'right' ? 'right' : 'left';
    if (Number.isFinite(value.swingProjectionUnits) && value.swingProjectionUnits > 0) {
      out.swingProjectionUnits = value.swingProjectionUnits;
    }
  }
  if (value.kind === 'pocket') out.slideDirection = value.slideDirection === 'right' ? 'right' : 'left';
  if (value.kind === 'bifold_single') out.foldSide = value.foldSide === 'right' ? 'right' : 'left';
  for (const key of ['bifoldStackProjectionUnits', 'bypassClearLeftUnits', 'bypassClearRightUnits']) {
    if (Number.isFinite(value[key]) && value[key] >= 0) out[key] = value[key];
  }
  return out;
}

function cleanCeilingOpening(value) {
  if (!value || typeof value !== 'object' || !['attic_hatch', 'attic_stairs', 'ceiling_fan', 'ceiling_light', 'ceiling_sprinkler'].includes(value.kind)) return null;
  const referenceWallIds = Array.isArray(value.referenceWallIds)
    ? value.referenceWallIds.filter((id) => typeof id === 'string').slice(0, 2)
    : [];
  if (!['xUnits', 'yUnits', 'widthUnits', 'lengthUnits'].every((key) => Number.isFinite(value[key]))
    || value.xUnits < 0 || value.yUnits < 0 || value.widthUnits <= 0 || value.lengthUnits <= 0
    || referenceWallIds.length !== 2 || referenceWallIds[0] === referenceWallIds[1]) return null;
  const fanDiameter = value.kind === 'ceiling_fan'
    ? (Number.isFinite(value.diameterUnits) && value.diameterUnits > 0 ? value.diameterUnits : value.widthUnits)
    : null;
  const fanFromFloor = value.kind === 'ceiling_fan' && Number.isFinite(value.fromFloorUnits) && value.fromFloorUnits >= 0
    ? value.fromFloorUnits : null;
  return {
    id: typeof value.id === 'string' ? value.id : null, kind: value.kind,
    referenceWallIds,
    xUnits: value.xUnits, yUnits: value.yUnits,
    widthUnits: value.kind === 'ceiling_fan' ? fanDiameter : value.widthUnits,
    lengthUnits: value.kind === 'ceiling_fan' ? fanDiameter : value.lengthUnits,
    heightUnits: value.kind === 'ceiling_fan' && fanFromFloor !== null
      ? 1
      : (Number.isFinite(value.heightUnits) && value.heightUnits > 0 ? value.heightUnits : 1),
    ...(value.kind === 'ceiling_fan' ? { diameterUnits: fanDiameter, fromFloorUnits: fanFromFloor } : {}),
    hatchStyle: typeof value.hatchStyle === 'string' ? value.hatchStyle : (value.kind === 'attic_stairs' ? 'drop_down' : 'hinged'),
    ladderFloorOffsetUnits: Number.isFinite(value.ladderFloorOffsetUnits) && value.ladderFloorOffsetUnits >= 0 ? value.ladderFloorOffsetUnits : null,
    ladderProjectionUnits: Number.isFinite(value.ladderProjectionUnits) && value.ladderProjectionUnits >= 0 ? value.ladderProjectionUnits : null,
    showLadderExtended: value.kind === 'attic_stairs' ? value.showLadderExtended !== false : false,
  };
}

/**
 * The identity key for one SPACE. Case- and whitespace-insensitive so
 * "Master WIC" and "master wic " cannot become two states of the same space.
 * Legacy duplicate records may still share a key until the designer renames or
 * deletes the extras.
 */
export function spaceKey(meta) {
  return [meta?.client, meta?.opportunity, meta?.space]
    .map((v) => clean(v).toLowerCase())
    .join('\0');
}

/**
 * True when `candidate` would give two real sketches the same
 * Client/Opportunity/Space identity. Templates are not spaces and an existing
 * document may exclude itself while it is being renamed.
 */
export function spaceIdentityConflict(docs, candidate, { exceptId = null } = {}) {
  const key = spaceKey(candidate);
  return (Array.isArray(docs) ? docs : []).some((doc) => doc
    && doc.id !== exceptId
    && doc.isTemplate !== true
    && spaceKey(doc) === key);
}

/**
 * The stable standalone key for one client opportunity. SSH will eventually
 * supply its own opportunity identifier, but the local app must be able to
 * group and reopen the same work while offline before that host exists.
 */
export function opportunityKey(meta) {
  return [meta?.client, meta?.opportunity]
    .map((v) => clean(v).toLowerCase())
    .join('\0');
}

/**
 * The next auto version label for a space, given its existing versions. Counts
 * the siblings and returns "Version N+1", skipping any label already in use so
 * a delete-then-add never collides. Used when the designer adds a version
 * without naming it (they chose named-with-auto-default).
 *
 * @param {Array<{version?:string}>} siblings docs already in the space
 */
export function nextVersionLabel(siblings = []) {
  const taken = new Set(siblings.map((d) => clean(d.version)));
  let n = siblings.length + 1;
  while (taken.has(`Version ${n}`)) n += 1;
  return `Version ${n}`;
}

/**
 * Resolves the explicitly preferred version of a space. A space may
 * intentionally have no preferred version; in that case this returns null even
 * when only one version exists. If malformed legacy data flags more than one,
 * the newest flagged version wins deterministically until the designer chooses
 * again and the UI normalizes the flags.
 *
 * @param {object[]} siblings docs in one space
 */
export function preferredVersionId(siblings = []) {
  if (siblings.length === 0) return null;
  const flagged = siblings.filter((d) => d.isPreferred === true);
  if (flagged.length === 0) return null;
  if (flagged.length === 1) return flagged[0].id;
  return [...flagged].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0].id;
}

/**
 * @param {object} state the sketch-model state
 * @param {SketchMeta} meta identity of the sketch being saved
 * @param {string} [nowIso] ISO timestamp (injected so tests are deterministic)
 * @returns {object} a JSON-safe document
 */
export function serializeSketch(state, meta, nowIso) {
  return {
    schemaVersion: SKETCH_SCHEMA_VERSION,
    id: meta.id,
    client: clean(meta.client),
    opportunity: clean(meta.opportunity),
    space: clean(meta.space),
    // Legacy wire compatibility only (ADR 0106). The product exposes one state
    // per named space and normal new/copied spaces use Version 1 / false.
    version: clean(meta.version) || 'Version 1',
    isPreferred: meta.isPreferred === true,
    // A template is a reusable starting point, not a real space's sketch:
    // opening one spawns an independent copy rather than editing the template
    // (ADR 0029). templateName is its library label; identity fields stay blank.
    isTemplate: meta.isTemplate === true,
    templateName: clean(meta.templateName),
    // Curated library order. A finite non-negative rank places a template ahead
    // of unranked templates; normal sketches never carry a rank.
    templateRank: meta.isTemplate === true && Number.isInteger(meta.templateRank) && meta.templateRank >= 0
      ? meta.templateRank : null,
    updatedAt: nowIso ?? new Date().toISOString(),
    selectedWallId: state.selectedWallId ?? null,
    // Unlike the transient last-solve value (`pxPerUnit`), this is the durable
    // scale established by the initial longest-wall measurement. Templates
    // carry it so their actual dimensions do not change when used.
    scalePxPerUnit: Number.isFinite(state.scalePxPerUnit) && state.scalePxPerUnit > 0
      ? state.scalePxPerUnit : null,
    // A closed plan is the normal room-design default. Explicit false preserves
    // an intentionally open partial capture.
    snapClosed: state.snapClosed !== false,
    closedPerimeter: state.closedPerimeter === true,
    // The designer's Wall 1 pin (null = automatic rule). Durable, not derived:
    // losing it on reload would resurrect the very misnumbering it corrects.
    wallOneOverrideId: state.wallOneOverrideId ?? null,
    ceilingHeightUnits: state.ceilingHeightUnits ?? null,
    ceilingFlat: state.ceilingFlat !== false,
    doorHeightUnits: state.doorHeightUnits ?? null,
    // Explicit per-line show/hide choices; absent labels use the rule default.
    // (Replaces the old hiddenHelperLines array — see hydrateSketch for the
    // legacy conversion.)
    helperLineOverrides: cleanOverrides(state.helperLineOverrides) ?? {},
    customHelperLines: cleanCustomHelperLines(state.customHelperLines) ?? [],
    obstructions: (state.obstructions ?? []).map((o) => ({
      id: o.id,
      wallId: o.wallId,
      type: o.type,
      label: o.label ?? '',
      offsetAlongWall: o.offsetAlongWall,
      offsetFromFloor: o.offsetFromFloor,
      widthUnits: o.widthUnits,
      heightUnits: o.heightUnits,
      depthUnits: o.depthUnits ?? null,
      faceOffsetUnits: Number.isFinite(o.faceOffsetUnits) ? o.faceOffsetUnits : 0,
      referenceEdge: o.referenceEdge === 'right' ? 'right' : 'left',
      locked: o.locked === true,
      opening: cleanOpening(o.opening),
    })),
    ceilingOpenings: (state.ceilingOpenings ?? []).map(cleanCeilingOpening).filter((o) => o?.id),
    // Per-wall depth helper lines on the plan (ADR 0024) + grid toggle.
    planHelperLines: (state.planHelperLines ?? [])
      .filter((h) => h && typeof h.wallId === 'string' && Number.isFinite(h.offsetUnits) && h.offsetUnits > 0)
      .map((h) => ({ id: h.id, wallId: h.wallId, offsetUnits: h.offsetUnits })),
    planHelperLinesVisible: state.planHelperLinesVisible !== false,
    gridVisible: state.gridVisible !== false,
    crownEnabled: state.crownEnabled === true,
    baseTrimEnabled: state.baseTrimEnabled === true,
    // The two elevation DIMENSION toggles (ADR 0061 item E), sketch-level
    // exactly like gridVisible/storageSummaryVisible beside them. Default OFF:
    // they are extra figures over the drawing, opted into per sketch.
    shelfSpacingDimsVisible: state.shelfSpacingDimsVisible === true,
    drawerFaceDimsVisible: state.drawerFaceDimsVisible === true,
    storageSummaryVisible: state.storageSummaryVisible !== false,
    // Interior/partition walls (ADR 0032): keyed by their footprint segment id,
    // storing only the protrusion depth (thickness = that segment's length).
    partitions: (state.partitions ?? [])
      .filter((p) => p && typeof p.segmentId === 'string' && p.depthUnits > 0)
      .map((p) => ({ segmentId: p.segmentId, depthUnits: p.depthUnits, includeInElevation: p.includeInElevation !== false })),
    // Standard section-type labels anchored to a wall (ADR 0034).
    wallLabels: (state.wallLabels ?? [])
      .filter((l) => l && typeof l.wallId === 'string' && String(l.text ?? '').trim())
      .map((l) => ({
        id: l.id,
        wallId: l.wallId,
        offsetAlongWall: Number.isFinite(l.offsetAlongWall) ? l.offsetAlongWall : 0,
        offsetFromFloor: Number.isFinite(l.offsetFromFloor) ? l.offsetFromFloor : 0,
        text: String(l.text),
        color: l.color ?? '#111827',
      })),
    // Wall sections (ADR 0037): typed cabinetry units laid out along a wall.
    // Only the INPUTS persist (order, lock, overrides); computed openings are
    // derived fresh by the layout solver on every render, never stored.
    sections: (state.sections ?? [])
      .filter((s) => s && typeof s.wallId === 'string' && String(s.typeCode ?? '').trim())
      .map((s) => ({
        id: s.id,
        wallId: s.wallId,
        order: Number.isFinite(s.order) ? s.order : 0,
        typeCode: String(s.typeCode),
        widthUnits: s.locked && Number.isFinite(s.widthUnits) && s.widthUnits > 0 ? s.widthUnits : null,
        locked: s.locked === true && Number.isFinite(s.widthUnits) && s.widthUnits > 0,
        fullPanelAfter: s.fullPanelAfter === true,
        allowWide: s.allowWide === true,
        depthUnits: Number.isFinite(s.depthUnits) && s.depthUnits > 0 ? s.depthUnits : null,
        heightUnits: Number.isFinite(s.heightUnits) && s.heightUnits > 0 ? s.heightUnits : null,
        shelfCount: Number.isInteger(s.shelfCount) && s.shelfCount >= 0 ? s.shelfCount : null,
        drawerCount: Number.isInteger(s.drawerCount) && s.drawerCount >= 0 ? s.drawerCount : null,
        pairWidthUnits: Number.isFinite(s.pairWidthUnits) && s.pairWidthUnits > 0 ? s.pairWidthUnits : null,
        assignedTo: s.assignedTo === 0 || s.assignedTo === 1 ? s.assignedTo : null,
        intoHanging: s.intoHanging === true,
        intoHangingStart: typeof s.intoHangingStart === 'boolean' ? s.intoHangingStart : null,
        intoHangingEnd: typeof s.intoHangingEnd === 'boolean' ? s.intoHangingEnd : null,
        // Vertical stacking (ADR 0041): the section this one RESTS on, and a
        // base's countertop override (null = inherit the space setting).
        stackOnId: typeof s.stackOnId === 'string' && s.stackOnId ? s.stackOnId : null,
        stackOffsetUnits: Number.isFinite(s.stackOffsetUnits) && s.stackOffsetUnits > 0 ? s.stackOffsetUnits : 0,
        // A "to height" pinned on this unit (owner, 2026-08-04).
        targetTopUnits: Number.isFinite(s.targetTopUnits) && s.targetTopUnits > 0 ? s.targetTopUnits : null,
        // L-shelf corner attachment (ADR 0042).
        secondWallId: typeof s.secondWallId === 'string' && s.secondWallId ? s.secondWallId : null,
        legTwoUnits: Number.isFinite(s.legTwoUnits) && s.legTwoUnits > 0 ? s.legTwoUnits : null,
        legTwoDepthUnits: Number.isFinite(s.legTwoDepthUnits) && s.legTwoDepthUnits > 0 ? s.legTwoDepthUnits : null,
        // Elevation caption nudge (owner, 2026-08-28): room units, + = down.
        elevCodeDyUnits: Number.isFinite(s.elevCodeDyUnits) && s.elevCodeDyUnits !== 0 ? s.elevCodeDyUnits : null,
        countertopThicknessUnits: Number.isFinite(s.countertopThicknessUnits) && s.countertopThicknessUnits >= 0
          ? s.countertopThicknessUnits : null,
        // Composable interior + doors (ADR 0054). `interior` null is
        // LOAD-BEARING: it marks a legacy section that derives its composition
        // from its type preset + counts at read; an array (even empty) is a
        // real composition and round-trips verbatim, element accessories
        // included. This whitelist is one of the three that must carry the
        // fields or they silently drop (store serialize/hydrate + sync mapping).
        interior: normalizeInterior(s.interior),
        doors: normalizeDoors(s.doors),
        // "Required holes only" (ADR 0061 item B): the shop drills the drawer
        // banks and the top/bottom shelves but NOT the 32 mm rows, so shelves
        // in this section take exact designer positions instead of snapping.
        // Third of the three whitelists this field must appear in.
        requiredHolesOnly: s.requiredHolesOnly === true,
        // Whether the section has a bottom shelf — drives its base trim.
        bottomShelf: s.bottomShelf !== false,
        connectingShelfStart: cleanConnectingShelf(s.connectingShelfStart),
        connectingShelfEnd: cleanConnectingShelf(s.connectingShelfEnd),
        // Bucket-conversion provenance (ADR 0059): the card this section was
        // placed from, so removing it returns a unit to the tray.
        fromBucket: typeof s.fromBucket === 'string' && s.fromBucket ? s.fromBucket : null,
      })),
    // Shared-space split (A/B, ADR 0039 addendum): the toggle + names.
    sharedSpace: {
      enabled: state.sharedSpace?.enabled === true,
      persons: [
        String(state.sharedSpace?.persons?.[0] ?? 'A'),
        String(state.sharedSpace?.persons?.[1] ?? 'B'),
      ],
    },
    // What the CLIENT needs, per storage row (ADR 0043) — the other half of the
    // storage summary's need-vs-capacity comparison.
    // Sloped ceilings (ADR 0044): heights live on the chain's CORNERS, keyed by
    // the segment whose end each one is, plus the open run's first start and any
    // interior breakpoints.
    cornerHeights: cleanCornerHeights(state.cornerHeights),
    chainStartHeightUnits: Number.isFinite(state.chainStartHeightUnits) && state.chainStartHeightUnits > 0
      ? state.chainStartHeightUnits : null,
    wallHeightBreaks: cleanWallHeightBreaks(state.wallHeightBreaks),
    storageNeeds: cleanStorageNeeds(state.storageNeeds),
    storageNeedsSource: cleanStorageNeedsSource(state.storageNeedsSource),
    // Current/Need (owner's words). `has`/`wants` were the values for the first
    // hours of this feature; normalise rather than stranding those sketches.
    storageCompareTo: ({ current: 'current', has: 'current', need: 'need', wants: 'need' })[state.storageCompareTo] ?? 'need',
    // Space-level section height override (84" space etc.); null = none.
    spaceHeightUnits: Number.isFinite(state.spaceHeightUnits) && state.spaceHeightUnits > 0 ? state.spaceHeightUnits : null,
    platformHeightUnits: Number.isFinite(state.platformHeightUnits) && state.platformHeightUnits > 0 ? state.platformHeightUnits : 0,
    // Vertical stacking (ADR 0041): space countertop thickness, and the
    // to-the-ceiling mode with its crown allowance (null = the rule default).
    countertopThicknessUnits: Number.isFinite(state.countertopThicknessUnits) && state.countertopThicknessUnits > 0
      ? state.countertopThicknessUnits : 0,
    // "To target height" (ADR 0041, revised 2026-07-29 — was "to the ceiling").
    toTargetHeight: state.toTargetHeight === true,
    stackTargetHeightUnits: Number.isFinite(state.stackTargetHeightUnits) && state.stackTargetHeightUnits > 0
      ? state.stackTargetHeightUnits : null,
    // Per-wall overrides of that target (owner, 2026-08-04). Sparse: only walls
    // that actually disagree with the space appear.
    wallStackTargets: Object.fromEntries(
      Object.entries(state.wallStackTargets ?? {})
        .filter(([, v]) => Number.isFinite(v) && v > 0),
    ),
    crownAllowanceUnits: Number.isFinite(state.crownAllowanceUnits) && state.crownAllowanceUnits >= 0
      ? state.crownAllowanceUnits : null,
    // Per-space trim-height overrides (owner, 2026-08-28); null = rule default.
    crownMouldingHeightUnits: Number.isFinite(state.crownMouldingHeightUnits) && state.crownMouldingHeightUnits > 0
      ? state.crownMouldingHeightUnits : null,
    baseTrimHeightUnits: Number.isFinite(state.baseTrimHeightUnits) && state.baseTrimHeightUnits > 0
      ? state.baseTrimHeightUnits : null,
    // Space pricing (ADR 0058): the designer's material level + countertop
    // material picks, and the last computed low/high totals stamp — what the
    // space's PREFERRED version carries toward the ballpark (ADR 0021). One of
    // the three whitelists that must carry the field or it silently drops.
    pricing: cleanPricing(state.pricing),
    // Basic mode (ADR 0059): the view mode, space type, span bounds and the
    // staged bucket cards — same three-whitelist rule as pricing.
    basic: cleanBasic(state.basic),
    // Freehand strokes AND typed text notes over the plan/elevations (ADR
    // 0023/0033). A text note is one anchor point + a string; a stroke is 2+.
    annotations: (state.annotations ?? [])
      .filter((a) => a && Array.isArray(a.points) && (a.kind === 'text' ? a.points.length >= 1 && String(a.text ?? '').trim() : a.points.length >= 2))
      .map((a) => ({
        id: a.id,
        surface: a.surface === 'elevation' ? 'elevation' : 'plan',
        color: a.color ?? '#e5484d',
        width: Number.isFinite(a.width) ? a.width : 2,
        wallId: typeof a.wallId === 'string' ? a.wallId : null,
        points: a.points.map((p) => ({ x: p.x, y: p.y })),
        // fontSize rides with the note (ADR 0033 addendum, 2026-07-29); absent
        // on every note written before the picker existed, which reads as the
        // stylesheet default rather than as zero.
        ...(a.kind === 'text'
          ? { kind: 'text', text: String(a.text ?? ''), fontSize: Number.isFinite(a.fontSize) && a.fontSize > 0 ? a.fontSize : null }
          : {}),
      })),
    walls: (state.walls ?? []).map((w) => ({
      id: w.id,
      segmentKind: w.segmentKind ?? 'wall',
      internalAngle: w.internalAngle,
      angleDeg: w.angleDeg,
      start: { x: w.start.x, y: w.start.y },
      end: { x: w.end.x, y: w.end.y },
      isMeasured: !!w.isMeasured,
      // Provenance is separate from `isMeasured`: that legacy flag means the
      // solver must hold this value, while this state says whether the figure
      // was actually captured here or inherited from a copied space.
      measurementState: cleanMeasurementState(w.measurementState),
      lengthUnits: w.lengthUnits ?? null,
      sequenceIndex: w.sequenceIndex,
      wallNumber: w.wallNumber ?? null,
      includeInElevation: w.includeInElevation !== false,
      // Locked walls keep their displayed span while snap-close reconciles the
      // remaining unlocked perimeter walls.
      snapLengthLocked: w.snapLengthLocked === true,
      // Per-wall thickness override in inches; NULL = use the shop default.
      // Only a positive value is stored (the DB CHECK is `NULL OR > 0`), so a
      // stray 0 can never be persisted (ADR 0030/0031).
      thicknessUnits: Number.isFinite(w.thicknessUnits) && w.thicknessUnits > 0 ? w.thicknessUnits : null,
    })),
  };
}

/**
 * Validates and unpacks a stored document's GEOMETRY into the fields the model
 * restores. Returns null when the document is missing, the wrong version, or
 * malformed — a corrupt or future document must never crash startup; the app
 * just starts empty instead.
 *
 * @returns {{ walls: object[], selectedWallId: string|null } | null}
 */
export function hydrateSketch(doc, { makeId } = {}) {
  // Interior-element ids mint here for LEGACY docs (per-space doors
  // foundation, 2026-08-29): a doc written before ids existed gains them
  // once at hydrate; the auto-save then persists them and every later load
  // finds them present. Injectable for tests; the crypto fallback matches
  // the app's own newId() shape.
  const mintId = typeof makeId === 'function' ? makeId
    : () => (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `el-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  if (!doc || typeof doc !== 'object') return null;
  if (doc.schemaVersion !== SKETCH_SCHEMA_VERSION) return null;
  if (!Array.isArray(doc.walls)) return null;

  const walls = [];
  for (const w of doc.walls) {
    if (!isValidSegment(w)) return null; // all-or-nothing: a bad segment voids the load
    walls.push({
      id: w.id,
      segmentKind: w.segmentKind ?? 'wall',
      internalAngle: w.internalAngle,
      angleDeg: w.angleDeg,
      start: { x: w.start.x, y: w.start.y },
      end: { x: w.end.x, y: w.end.y },
      isMeasured: !!w.isMeasured,
      measurementState: cleanMeasurementState(w.measurementState),
      lengthUnits: w.lengthUnits ?? null,
      sequenceIndex: w.sequenceIndex,
      wallNumber: w.wallNumber ?? null,
      includeInElevation: w.includeInElevation !== false, // default true for older docs
      snapLengthLocked: w.snapLengthLocked === true,
      // Only a positive override loads; absent/0/negative → undefined (default).
      ...(Number.isFinite(w.thicknessUnits) && w.thicknessUnits > 0 ? { thicknessUnits: w.thicknessUnits } : {}),
    });
  }

  const ids = new Set(walls.map((w) => w.id));
  // Obstructions, labels and sections may be hosted on a partition FACE
  // (`${footprintId}~A|B`, ADR 0035) — those hosts are as valid as a wall id.
  // Filtering by `ids` alone silently dropped face-hosted items on every
  // reload; derive the face ids from the surviving partitions instead.
  const validHosts = new Set(ids);
  if (Array.isArray(doc.partitions)) {
    for (const p of doc.partitions) {
      if (p && ids.has(p.segmentId)) {
        validHosts.add(`${p.segmentId}~A`);
        validHosts.add(`${p.segmentId}~B`);
      }
    }
  }
  const selectedWallId = ids.has(doc.selectedWallId) ? doc.selectedWallId : null;
  const ceilingHeightUnits = Number.isFinite(doc.ceilingHeightUnits) ? doc.ceilingHeightUnits : null;
  const cornerHeights = cleanCornerHeights(doc.cornerHeights);
  const chainStartHeightUnits = Number.isFinite(doc.chainStartHeightUnits) && doc.chainStartHeightUnits > 0
    ? doc.chainStartHeightUnits : null;
  const wallHeightBreaks = cleanWallHeightBreaks(doc.wallHeightBreaks);
  const differsFromRoom = (height) => Number.isFinite(height)
    && (ceilingHeightUnits === null || Math.abs(height - ceilingHeightUnits) > 0.01);
  const legacyHasSlope = Object.values(cornerHeights).some(differsFromRoom)
    || differsFromRoom(chainStartHeightUnits)
    || Object.values(wallHeightBreaks).some((points) => points.some((point) => differsFromRoom(point.heightUnits)));
  return {
    walls,
    selectedWallId,
    scalePxPerUnit: Number.isFinite(doc.scalePxPerUnit) && doc.scalePxPerUnit > 0
      ? doc.scalePxPerUnit : null,
    snapClosed: doc.snapClosed !== false,
    closedPerimeter: doc.closedPerimeter === true,
    // Same referential check as the selection: a pin naming a vanished wall is
    // dropped here so the model never starts up claiming a pin with no target.
    wallOneOverrideId: ids.has(doc.wallOneOverrideId) ? doc.wallOneOverrideId : null,
    ceilingHeightUnits,
    // Before this toggle existed, actual slope data is authoritative: expose
    // its editor instead of defaulting on and silently hiding the old work.
    ceilingFlat: typeof doc.ceilingFlat === 'boolean' ? doc.ceilingFlat : !legacyHasSlope,
    doorHeightUnits: Number.isFinite(doc.doorHeightUnits) ? doc.doorHeightUnits : null,
    // Explicit show/hide choices. Docs saved before overrides existed carry a
    // hiddenHelperLines array instead; each hidden label becomes an explicit
    // "hide" so the designer's old choices survive the semantics change. (Old
    // docs' UNhidden lines get no entry — they follow the current default,
    // which is the desired reading of "never explicitly chose".)
    helperLineOverrides: cleanOverrides(doc.helperLineOverrides)
      ?? (Array.isArray(doc.hiddenHelperLines)
        ? Object.fromEntries(doc.hiddenHelperLines.filter((l) => typeof l === 'string').map((l) => [l, false]))
        : {}),
    // 3 custom lines since 2026-07-28; a pre-that doc carried a single
    // customHelperLineUnits/Visible pair, migrated into slot 0 here.
    customHelperLines: cleanCustomHelperLines(doc.customHelperLines)
      ?? (Number.isFinite(doc.customHelperLineUnits) && doc.customHelperLineUnits > 0
        ? [{ units: doc.customHelperLineUnits, visible: doc.customHelperVisible !== false }]
        : []),
    // Keep only well-formed obstructions still attached to a wall that exists —
    // a stale one would otherwise be invisible but still saved forever.
    obstructions: Array.isArray(doc.obstructions)
      ? doc.obstructions
          .filter(
            (o) =>
              o &&
              typeof o.id === 'string' &&
              validHosts.has(o.wallId) &&
              Number.isFinite(o.offsetAlongWall) &&
              Number.isFinite(o.offsetFromFloor) &&
              Number.isFinite(o.widthUnits) &&
              Number.isFinite(o.heightUnits),
          )
          .map((o) => ({
            id: o.id,
            wallId: o.wallId,
            type: o.type ?? 'custom',
            label: o.label ?? '',
            offsetAlongWall: o.offsetAlongWall,
            offsetFromFloor: o.offsetFromFloor,
            widthUnits: o.widthUnits,
            heightUnits: o.heightUnits,
            depthUnits: Number.isFinite(o.depthUnits) ? o.depthUnits : null,
            faceOffsetUnits: Number.isFinite(o.faceOffsetUnits) ? o.faceOffsetUnits : 0,
            referenceEdge: o.referenceEdge === 'right' ? 'right' : 'left',
            locked: o.locked === true,
      opening: cleanOpening(o.opening),
          }))
      : [],
    ceilingOpenings: Array.isArray(doc.ceilingOpenings)
      ? doc.ceilingOpenings.map(cleanCeilingOpening)
        .filter((o) => o?.id && o.referenceWallIds.every((wallId) => ids.has(wallId)))
      : [],
    // Per-wall depth helper lines (ADR 0024): keep only ones on a wall that
    // still exists, like obstructions.
    planHelperLines: Array.isArray(doc.planHelperLines)
      ? doc.planHelperLines
          .filter((h) => h && typeof h.id === 'string' && ids.has(h.wallId) && Number.isFinite(h.offsetUnits) && h.offsetUnits > 0)
          .map((h) => ({ id: h.id, wallId: h.wallId, offsetUnits: h.offsetUnits }))
      : [],
    planHelperLinesVisible: doc.planHelperLinesVisible !== false,
    gridVisible: doc.gridVisible !== false,
    crownEnabled: doc.crownEnabled === true,
    baseTrimEnabled: doc.baseTrimEnabled === true,
    shelfSpacingDimsVisible: doc.shelfSpacingDimsVisible === true,
    drawerFaceDimsVisible: doc.drawerFaceDimsVisible === true,
    storageSummaryVisible: doc.storageSummaryVisible !== false,
    // Interior/partition walls (ADR 0032): keep only ones whose footprint
    // segment still exists (it must be an opening in the loaded chain).
    partitions: Array.isArray(doc.partitions)
      ? doc.partitions
          .filter((p) => p && ids.has(p.segmentId) && Number.isFinite(p.depthUnits) && p.depthUnits > 0)
          .map((p) => ({ segmentId: p.segmentId, depthUnits: p.depthUnits, includeInElevation: p.includeInElevation !== false }))
      : [],
    // Standard wall labels (ADR 0034): keep only ones on a surviving wall.
    wallLabels: Array.isArray(doc.wallLabels)
      ? doc.wallLabels
          .filter((l) => l && typeof l.id === 'string' && validHosts.has(l.wallId) && String(l.text ?? '').trim())
          .map((l) => ({
            id: l.id,
            wallId: l.wallId,
            offsetAlongWall: Number.isFinite(l.offsetAlongWall) ? l.offsetAlongWall : 0,
            offsetFromFloor: Number.isFinite(l.offsetFromFloor) ? l.offsetFromFloor : 0,
            text: String(l.text),
            color: typeof l.color === 'string' ? l.color : '#111827',
          }))
      : [],
    // Wall sections (ADR 0037): keep only ones on a surviving host (wall or
    // partition face). Missing depth/height fall back to the rule defaults at
    // render time, so nulls here are fine.
    sections: dropOrphanStacks(Array.isArray(doc.sections)
      ? doc.sections
          .filter((s) => s && typeof s.id === 'string' && validHosts.has(s.wallId) && String(s.typeCode ?? '').trim())
          .map((s) => ({
            id: s.id,
            wallId: s.wallId,
            order: Number.isFinite(s.order) ? s.order : 0,
            typeCode: String(s.typeCode),
            widthUnits: s.locked === true && Number.isFinite(s.widthUnits) && s.widthUnits > 0 ? s.widthUnits : null,
            locked: s.locked === true && Number.isFinite(s.widthUnits) && s.widthUnits > 0,
            fullPanelAfter: s.fullPanelAfter === true,
            allowWide: s.allowWide === true,
            depthUnits: Number.isFinite(s.depthUnits) && s.depthUnits > 0 ? s.depthUnits : null,
            heightUnits: Number.isFinite(s.heightUnits) && s.heightUnits > 0 ? s.heightUnits : null,
            shelfCount: Number.isInteger(s.shelfCount) && s.shelfCount >= 0 ? s.shelfCount : null,
            drawerCount: Number.isInteger(s.drawerCount) && s.drawerCount >= 0 ? s.drawerCount : null,
            pairWidthUnits: Number.isFinite(s.pairWidthUnits) && s.pairWidthUnits > 0 ? s.pairWidthUnits : null,
            assignedTo: s.assignedTo === 0 || s.assignedTo === 1 ? s.assignedTo : null,
            intoHanging: s.intoHanging === true,
            intoHangingStart: typeof s.intoHangingStart === 'boolean' ? s.intoHangingStart : null,
            intoHangingEnd: typeof s.intoHangingEnd === 'boolean' ? s.intoHangingEnd : null,
            stackOnId: typeof s.stackOnId === 'string' && s.stackOnId ? s.stackOnId : null,
            stackOffsetUnits: Number.isFinite(s.stackOffsetUnits) && s.stackOffsetUnits > 0 ? s.stackOffsetUnits : 0,
            targetTopUnits: Number.isFinite(s.targetTopUnits) && s.targetTopUnits > 0 ? s.targetTopUnits : null,
            // L-shelf corner attachment (ADR 0042): the second wall must still
            // exist, or the shelf falls back to an unattached single leg.
            secondWallId: typeof s.secondWallId === 'string' && validHosts.has(s.secondWallId) ? s.secondWallId : null,
            legTwoUnits: Number.isFinite(s.legTwoUnits) && s.legTwoUnits > 0 ? s.legTwoUnits : null,
            elevCodeDyUnits: Number.isFinite(s.elevCodeDyUnits) && s.elevCodeDyUnits !== 0 ? s.elevCodeDyUnits : null,
            legTwoDepthUnits: Number.isFinite(s.legTwoDepthUnits) && s.legTwoDepthUnits > 0 ? s.legTwoDepthUnits : null,
            countertopThicknessUnits: Number.isFinite(s.countertopThicknessUnits) && s.countertopThicknessUnits >= 0
              ? s.countertopThicknessUnits : null,
            // Composable interior + doors (ADR 0054): null = legacy section
            // (composition derived from preset + counts at read); an array is
            // authoritative and the counts above are its derived mirrors.
            interior: ensureInteriorElementIds(s.interior, mintId),
            doors: normalizeDoors(s.doors),
            // Per-section drilling mode (ADR 0061 item B).
            requiredHolesOnly: s.requiredHolesOnly === true,
            bottomShelf: s.bottomShelf !== false,
            connectingShelfStart: cleanConnectingShelf(s.connectingShelfStart),
            connectingShelfEnd: cleanConnectingShelf(s.connectingShelfEnd),
            fromBucket: typeof s.fromBucket === 'string' && s.fromBucket ? s.fromBucket : null,
          }))
      : []),
    cornerHeights,
    chainStartHeightUnits,
    wallHeightBreaks,
    storageNeeds: cleanStorageNeeds(doc.storageNeeds),
    storageNeedsSource: cleanStorageNeedsSource(doc.storageNeedsSource),
    // Current/Need (owner's words). `has`/`wants` were the values for the first
    // hours of this feature; normalise rather than stranding those sketches.
    storageCompareTo: ({ current: 'current', has: 'current', need: 'need', wants: 'need' })[doc.storageCompareTo] ?? 'need',
    spaceHeightUnits: Number.isFinite(doc.spaceHeightUnits) && doc.spaceHeightUnits > 0 ? doc.spaceHeightUnits : null,
    platformHeightUnits: Number.isFinite(doc.platformHeightUnits) && doc.platformHeightUnits > 0 ? doc.platformHeightUnits : 0,
    countertopThicknessUnits: Number.isFinite(doc.countertopThicknessUnits) && doc.countertopThicknessUnits > 0
      ? doc.countertopThicknessUnits : 0,
    // A pre-2026-07-29 sketch stored `toCeiling`; the mode is the same, and its
    // null target resolves to the room ceiling, so those sketches load unchanged.
    toTargetHeight: doc.toTargetHeight === true || doc.toCeiling === true,
    stackTargetHeightUnits: Number.isFinite(doc.stackTargetHeightUnits) && doc.stackTargetHeightUnits > 0
      ? doc.stackTargetHeightUnits : null,
    // Absent on older docs → no overrides, so the whole room follows the space.
    wallStackTargets: Object.fromEntries(
      Object.entries(doc.wallStackTargets ?? {})
        .filter(([k, v]) => validHosts.has(k) && Number.isFinite(v) && v > 0),
    ),
    crownAllowanceUnits: Number.isFinite(doc.crownAllowanceUnits) && doc.crownAllowanceUnits >= 0
      ? doc.crownAllowanceUnits : null,
    // Absent on pre-2026-08-28 docs → follow the rule defaults.
    crownMouldingHeightUnits: Number.isFinite(doc.crownMouldingHeightUnits) && doc.crownMouldingHeightUnits > 0
      ? doc.crownMouldingHeightUnits : null,
    baseTrimHeightUnits: Number.isFinite(doc.baseTrimHeightUnits) && doc.baseTrimHeightUnits > 0
      ? doc.baseTrimHeightUnits : null,
    // Space pricing (ADR 0058): absent on every pre-4e doc → the defaults
    // (level 1, no countertop material, no stamped totals).
    pricing: cleanPricing(doc.pricing),
    // Basic mode (ADR 0059): absent on older docs → Detailed with an empty
    // tray. A card ASSIGNED to a vanished wall keeps its units and drops back
    // to unassigned (assignment is spatial context, not identity).
    basic: cleanBasic(doc.basic, validHosts),
    // Shared-space split: absent on older docs → not shared, default names.
    sharedSpace: {
      enabled: doc.sharedSpace?.enabled === true,
      persons: [
        String(doc.sharedSpace?.persons?.[0] ?? 'A') || 'A',
        String(doc.sharedSpace?.persons?.[1] ?? 'B') || 'B',
      ],
    },
    // Freehand strokes AND typed text notes (ADR 0023/0033). Keep well-formed
    // ones: a stroke needs 2+ points; a text note needs 1 point and a string.
    annotations: Array.isArray(doc.annotations)
      ? doc.annotations
          .filter((a) => a && typeof a.id === 'string' && Array.isArray(a.points))
          .map((a) => ({
            id: a.id,
            surface: a.surface === 'elevation' ? 'elevation' : 'plan',
            color: typeof a.color === 'string' ? a.color : '#e5484d',
            width: Number.isFinite(a.width) && a.width > 0 ? a.width : 2,
            wallId: typeof a.wallId === 'string' ? a.wallId : null,
            points: a.points.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y)).map((p) => ({ x: p.x, y: p.y })),
            ...(a.kind === 'text' && String(a.text ?? '').trim()
              ? { kind: 'text', text: String(a.text), fontSize: textSizeInches(a.fontSize) }
              : {}),
          }))
          .filter((a) => (a.kind === 'text' ? a.points.length >= 1 : a.points.length >= 2))
      : [],
  };
}

/**
 * Copies a sketch document into an independent new one.
 *
 * The use case is a second space in the same house: same client, same
 * opportunity, often a near-identical room — redrawing it by hand is the
 * waste this removes. Client and Opportunity therefore carry over untouched;
 * only the Space is renamed, because that is precisely what differs.
 *
 * EVERY INTERNAL ID IS REMAPPED — walls, openings, obstructions, partitions,
 * helper lines, wall labels, sections, annotations — and every reference to
 * them rewritten: wall-hosted items' `wallId` (including partition-FACE hosts,
 * `${segmentId}~A|B`, ADR 0035), a partition's `segmentId`, a section's
 * `stackOnId` (its stack support, ADR 0041) and `secondWallId` (an L-shelf's
 * other wall, ADR 0042), the wall-id-KEYED maps `cornerHeights` /
 * `wallHeightBreaks` (sloped ceilings, ADR 0044) and `wallStackTargets`
 * (per-wall stack targets, ADR 0050), plus `selectedWallId` and
 * `wallOneOverrideId`. This is not cosmetic, twice over. Wall ids become
 * primary keys in `sketch.walls` when the sync engine lands
 * (docs/ARCHITECTURE.md); two sketches sharing wall ids would collide on push,
 * and a field-level last-write-wins merge would then let edits to one room
 * silently overwrite the other. And a reference left pointing at an OLD id is
 * an orphan in the copy — hydrateSketch prunes items on unknown hosts, so a
 * partially-remapped copy silently loses every section, partition, label and
 * helper line on first load (the 2026-08 data-loss bug), and un-remapped
 * cornerHeights/wallHeightBreaks flatten a sloped ceiling to the room height.
 *
 * An item whose host did not survive is DROPPED rather than given a
 * freshly-minted parent id, which would silently reattach it to nothing —
 * the same rule hydrateSketch applies, just applied at copy time.
 *
 * Copy modes:
 *   - NEW SPACE (`space` given, N4): same Client/Opportunity, the given Space,
 *     compatibility label "Version 1", and no preferred-selection linkage.
 *   - SAVE AS TEMPLATE (`asTemplate`, ADR 0029): geometry only, becomes a
 *     library template. Identity is CLEARED (a template is not tied to any
 *     client/opportunity/space) and `isTemplate: true` with `templateName`.
 *   - USE TEMPLATE (`fromTemplate`, ADR 0029): the inverse — a template's
 *     geometry becomes a fresh, blank-identity normal sketch (Version 1,
 *     preferred, `isTemplate: false`) for the designer to assign and edit.
 *
 * @param {object} doc a stored sketch document
 * @param {object} options
 * @param {string} options.id  the new sketch's id
 * @param {() => string} options.makeId  factory for new wall/obstruction ids
 * @param {string} [options.nowIso]  timestamp for the copy
 * @param {string} [options.space]  NEW SPACE mode: the new space's name
 * @param {boolean} [options.asTemplate]  SAVE AS TEMPLATE mode
 * @param {string} [options.templateName]  the template's library name
 * @param {boolean} [options.fromTemplate]  USE TEMPLATE mode
 * @returns {object|null} the new document, or null if the source is unusable
 */
export function duplicateSketchDoc(doc, { id, makeId, nowIso, space, asTemplate, templateName, fromTemplate } = {}) {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.walls) || typeof makeId !== 'function') return null;
  // N4 removed the normal-sketch default that minted another version under the
  // same space identity. A real copy must name a new space; template modes are
  // the only identity-free copies.
  if (!asTemplate && !fromTemplate && space === undefined) return null;

  // A stored sketch is JSON-safe by contract. Start from a DEEP copy so every
  // nested preference/list is independent even when no id remap is needed.
  // The old top-level spread left structures such as storage needs aliased when
  // callers held both documents in memory.
  const source = typeof structuredClone === 'function'
    ? structuredClone(doc)
    : JSON.parse(JSON.stringify(doc));

  // Do not rely on the app's global UUID minter happening to be unique across
  // sections. Copy identity is proven locally: every source id is reserved and
  // every id minted during this operation joins the same copy-scoped set.
  // `ensureInteriorElementIds` intentionally owns only a per-call `seen` set,
  // so this shared minter is what makes interiors across sections disjoint.
  const reservedIds = new Set();
  const collectIds = (value) => {
    if (Array.isArray(value)) { value.forEach(collectIds); return; }
    if (!value || typeof value !== 'object') return;
    if (typeof value.id === 'string' && value.id) reservedIds.add(value.id);
    Object.values(value).forEach(collectIds);
  };
  collectIds(source);
  if (typeof source.id === 'string' && source.id) reservedIds.add(source.id);
  const mintId = () => {
    for (let attempt = 0; attempt < 1000; attempt += 1) {
      const candidate = String(makeId());
      if (candidate && !reservedIds.has(candidate)) {
        reservedIds.add(candidate);
        return candidate;
      }
    }
    throw new Error('The copy id factory could not produce a fresh identity.');
  };

  const copyingSpace = space !== undefined && !asTemplate && !fromTemplate;
  const idMap = new Map();
  const walls = source.walls.map((w) => {
    const freshId = mintId();
    idMap.set(w.id, freshId);
    return {
      ...w,
      id: freshId,
      start: { ...w.start },
      end: { ...w.end },
      // The copied value remains usable geometry, but it did not describe this
      // physical space yet. Provenance is per wall so designers can clear it
      // one real re-measurement at a time.
      measurementState: copyingSpace && w.isMeasured && w.lengthUnits > 0
        ? 'inherited'
        : cleanMeasurementState(w.measurementState),
    };
  });

  // A host id is either a wall/segment id or a partition FACE id
  // (`${segmentId}~A|B`, ADR 0035). A face follows its footprint segment's
  // fresh id. Returns null for a host that maps to no surviving wall, so
  // callers drop the orphan instead of reattaching it to nothing.
  const mapHost = (hostId) => {
    if (typeof hostId !== 'string' || !hostId) return null;
    if (idMap.has(hostId)) return idMap.get(hostId);
    const face = /^(.+)~([AB])$/.exec(hostId);
    if (face && idMap.has(face[1])) return `${idMap.get(face[1])}~${face[2]}`;
    return null;
  };
  // Wall-id-KEYED maps (cornerHeights, wallHeightBreaks, wallStackTargets):
  // re-key every entry onto the fresh host id; entries on a vanished host drop.
  const mapKeyed = (obj) => {
    const out = {};
    for (const [key, value] of Object.entries(obj && typeof obj === 'object' ? obj : {})) {
      const fresh = mapHost(key);
      if (fresh) out[fresh] = Array.isArray(value) ? value.map((v) => ({ ...v })) : value;
    }
    return out;
  };

  const obstructions = (source.obstructions ?? [])
    .filter((o) => o && mapHost(o.wallId))
    .map((o) => ({ ...o, id: mintId(), wallId: mapHost(o.wallId) }));

  // Partitions are keyed by their footprint SEGMENT id (ADR 0032); their faces
  // (derived ids) host obstructions/labels/sections remapped via mapHost above.
  const partitions = (source.partitions ?? [])
    .filter((p) => p && idMap.has(p.segmentId))
    .map((p) => ({ ...p, segmentId: idMap.get(p.segmentId) }));

  // Plan depth-helper lines (ADR 0024) live on real walls only, like hydrate's
  // `ids.has` check — a face id is not a valid host for them.
  const planHelperLines = (source.planHelperLines ?? [])
    .filter((h) => h && idMap.has(h.wallId))
    .map((h) => ({ ...h, id: mintId(), wallId: idMap.get(h.wallId) }));

  const wallLabels = (source.wallLabels ?? [])
    .filter((l) => l && mapHost(l.wallId))
    .map((l) => ({ ...l, id: mintId(), wallId: mapHost(l.wallId) }));

  const ceilingOpenings = (source.ceilingOpenings ?? [])
    .filter((opening) => opening && Array.isArray(opening.referenceWallIds)
      && opening.referenceWallIds.length === 2 && opening.referenceWallIds.every((wallId) => idMap.has(wallId)))
    .map((opening) => ({
      ...opening,
      id: mintId(),
      referenceWallIds: opening.referenceWallIds.map((wallId) => idMap.get(wallId)),
    }));

  // Sections (ADR 0037): drop ones whose host vanished, then whole dangling
  // stacks (the same rule hydrate applies), THEN mint fresh ids — so a stack
  // support reference can never point at a section that was dropped. Every
  // reference is rewritten: the host wall/face, the stack support (stackOnId,
  // ADR 0041) and an L-shelf's second wall (secondWallId, ADR 0042).
  const basicCardIdMap = new Map((Array.isArray(source.basic?.cards) ? source.basic.cards : [])
    .filter((card) => card?.id)
    .map((card) => [card.id, mintId()]));
  const keptSections = dropOrphanStacks((source.sections ?? []).filter((s) => s && mapHost(s.wallId)));
  const sectionIdMap = new Map(keptSections.map((s) => [s.id, mintId()]));
  const sections = keptSections.map((s) => {
    // Doors address the spaces they cover by interior-element identity. Mint
    // the copy first, then rewrite both endpoints through that exact result;
    // carrying the door object unchanged would preserve it while destroying
    // its priceable extent (RS-18).
    const sourceInterior = Array.isArray(s.interior) ? s.interior : null;
    const interior = ensureInteriorElementIds(
      sourceInterior ? sourceInterior.map((element) => ({ ...element, id: null })) : sourceInterior,
      mintId,
    );
    const elementIdMap = new Map((sourceInterior ?? []).map((element, index) => [element.id, interior[index]?.id]));
    const sourceDoors = normalizeDoors(s.doors);
    const doors = sourceDoors ? {
      ...sourceDoors,
      ...(sourceDoors.fromElementId ? {
        fromElementId: elementIdMap.get(sourceDoors.fromElementId) ?? sourceDoors.fromElementId,
      } : {}),
      ...(sourceDoors.toElementId ? {
        toElementId: elementIdMap.get(sourceDoors.toElementId) ?? sourceDoors.toElementId,
      } : {}),
    } : null;
    return {
      ...s,
      id: sectionIdMap.get(s.id),
      wallId: mapHost(s.wallId),
      stackOnId: sectionIdMap.get(s.stackOnId) ?? null,
      secondWallId: mapHost(s.secondWallId),
      // `fromBucket` is the bucket KEY (for example "hanging"), not a card id.
      // It is provenance needed by Unplace and must remain semantic, not remap.
      fromBucket: s.fromBucket ?? null,
      // The shared copy-scoped minter—not ensureInteriorElementIds' per-call
      // set—guarantees uniqueness across every section in the copied space.
      interior,
      doors,
    };
  });

  // Annotations (ADR 0023/0033): elevation strokes/notes are anchored to a
  // wall; a plan annotation's wallId is null and stays null through mapHost.
  const annotations = (source.annotations ?? []).map((a) => ({
    ...a,
    id: mintId(),
    wallId: mapHost(a.wallId),
    points: Array.isArray(a.points) ? a.points.map((p) => ({ ...p })) : a.points,
  }));

  // Free-form footwear rows are editable entities inside the copied space,
  // not external client-inventory identities. Their ids must be fresh too;
  // `storageNeedsSource.importedSpaces[].id`, by contrast, names the external
  // client response and deliberately remains provenance.
  const storageNeeds = Object.fromEntries(Object.entries(source.storageNeeds && typeof source.storageNeeds === 'object'
    ? source.storageNeeds : {}).map(([key, entry]) => [key, {
    ...entry,
    ...(Array.isArray(entry?.shoeGroups) ? {
      shoeGroups: entry.shoeGroups.map((group) => ({ ...group, id: mintId() })),
    } : {}),
  }]));

  const newSpace = space !== undefined;

  // Identity + flags differ per mode; geometry (walls/obstructions/refs) is
  // remapped identically for all of them.
  let identity;
  if (asTemplate) {
    identity = {
      client: '', opportunity: '', space: '',
      version: 'Template', isPreferred: false,
      isTemplate: true, templateName: clean(templateName) || clean(sketchLabel(doc)) || 'Untitled template',
      templateRank: null,
    };
  } else if (fromTemplate) {
    identity = {
      client: '', opportunity: '', space: '',
      version: 'Version 1', isPreferred: false,
      isTemplate: false, templateName: '', templateRank: null,
    };
  } else {
    identity = {
      client: clean(source.client),
      opportunity: clean(source.opportunity),
      space: clean(newSpace ? space : source.space),
      version: 'Version 1',
      isPreferred: false,
      isTemplate: false, templateName: '', templateRank: null,
    };
  }

  return {
    ...source,
    id,
    ...identity,
    updatedAt: nowIso ?? new Date().toISOString(),
    walls,
    obstructions,
    partitions,
    planHelperLines,
    wallLabels,
    ceilingOpenings,
    sections,
    annotations,
    storageNeeds,
    // Sloped ceilings (ADR 0044) + per-wall stack targets (ADR 0050): keyed by
    // wall id, so the keys must follow the fresh ids or the copy silently
    // flattens to the room height / space target.
    cornerHeights: mapKeyed(source.cornerHeights),
    wallHeightBreaks: mapKeyed(source.wallHeightBreaks),
    wallStackTargets: mapKeyed(source.wallStackTargets),
    // Basic mode (ADR 0059): staged cards get fresh ids and their wall
    // ASSIGNMENT follows the fresh wall id — an assignment whose host did not
    // survive drops to unassigned (the units are the substance, not the wall).
    basic: source.basic && typeof source.basic === 'object'
      ? {
        ...source.basic,
        cards: (Array.isArray(source.basic.cards) ? source.basic.cards : [])
          .map((c) => ({ ...c, id: basicCardIdMap.get(c.id) ?? mintId(), wallId: mapHost(c.wallId) })),
      }
      : source.basic,
    selectedWallId: idMap.get(source.selectedWallId) ?? null,
    wallOneOverrideId: idMap.get(source.wallOneOverrideId) ?? null,
    // Everything else — heights (incl. chainStartHeightUnits, a scalar, not
    // keyed), helper-line choices, storage needs (keyed by TYPE, not by id) —
    // is per-sketch preference the designer would only have to re-enter, so it
    // rides along via the spread above.
  };
}

/**
 * Copies one saved design into a DIFFERENT named space. This is the supported
 * alternative-design workflow: one state per space, never another version of
 * the same identity. The legacy serializer fields remain for wire
 * compatibility, but no preferred/packet/contract selection rides the copy.
 */
export function copySpaceDoc(doc, existingDocs, options = {}) {
  const space = clean(options.space);
  const docs = Array.isArray(existingDocs) ? existingDocs : [];
  if (!space || !doc || !options.id || docs.some((candidate) => candidate?.id === options.id)
    || spaceIdentityConflict(docs, {
      client: doc.client,
      opportunity: doc.opportunity,
      space,
    })) return null;
  const copy = duplicateSketchDoc(doc, { ...options, space });
  if (!copy) return null;
  copy.version = 'Version 1';
  copy.isPreferred = false;
  // These fields do not exist in today's sketch contract. Removing the known
  // spellings here makes the boundary explicit if contract/packet linkage is
  // added later: a copied alternative must be selected independently.
  for (const key of ['contractId', 'contract_id', 'contractPacketId', 'contract_packet_id', 'packetId', 'packet_id']) {
    delete copy[key];
  }
  return copy;
}

/** Reads just the identity metadata from a stored document. */
export function readSketchMeta(doc) {
  return {
    id: doc.id,
    client: clean(doc.client),
    opportunity: clean(doc.opportunity),
    space: clean(doc.space),
    version: clean(doc.version) || 'Version 1',
    isPreferred: doc.isPreferred === true,
    isTemplate: doc.isTemplate === true,
    templateName: clean(doc.templateName),
    templateRank: doc.isTemplate === true && Number.isInteger(doc.templateRank) && doc.templateRank >= 0
      ? doc.templateRank : null,
    updatedAt: doc.updatedAt ?? null,
  };
}

/**
 * The display name for a sketch: "Client – Opportunity – Space", collapsing any
 * blank parts, or "Untitled sketch" when nothing is filled in yet. The version
 * is deliberately NOT included — the library groups by space and shows versions
 * beneath, so the label names the space.
 */
export function sketchLabel(meta) {
  const parts = [meta.client, meta.opportunity, meta.space].map(clean).filter(Boolean);
  return parts.length ? parts.join(' – ') : 'Untitled sketch';
}

/**
 * Helper-line overrides as {label: boolean} with junk entries dropped, or
 * null when the value isn't an object at all — the null lets hydrateSketch
 * distinguish "no such field (legacy doc)" from "present but empty".
 */
/**
 * Drops stacked sections (ADR 0041) whose support no longer exists — a unit
 * resting on a section that didn't survive hydrate has no horizontal slot of
 * its own, so it would be invisible but still saved forever (the same rule the
 * obstruction/partition anchors follow). Repeats until stable so a whole
 * dangling chain goes, not just its bottom link.
 */
function dropOrphanStacks(sections) {
  let kept = sections;
  for (;;) {
    const ids = new Set(kept.map((s) => s.id));
    const next = kept.filter((s) => !s.stackOnId || ids.has(s.stackOnId));
    if (next.length === kept.length) return next;
    kept = next;
  }
}

function cleanOverrides(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out = {};
  for (const [label, visible] of Object.entries(value)) {
    if (typeof visible === 'boolean') out[label] = visible;
  }
  return out;
}

/**
 * Normalizes the per-sketch custom helper lines to an array of
 * { units, visible }. Returns null when the field is absent (so hydrate can
 * fall back to the legacy single-line fields). Empty (units-less) slots are
 * dropped on save; positions aren't significant, only which lines exist.
 */
function cleanCustomHelperLines(value) {
  if (!Array.isArray(value)) return null;
  return value
    .filter((l) => l && typeof l === 'object' && Number.isFinite(l.units) && l.units > 0)
    .map((l) => ({ units: l.units, visible: l.visible !== false }));
}

function isValidSegment(w) {
  return (
    w &&
    typeof w.id === 'string' &&
    Number.isFinite(w.internalAngle) &&
    isPoint(w.start) &&
    isPoint(w.end)
  );
}

function isPoint(p) {
  return p && Number.isFinite(p.x) && Number.isFinite(p.y);
}

/**
 * Client storage requirements (ADR 0043), rebuilt field by field.
 *
 * Stored as `{ 'hang:short': { existing: {space,a,b}, needed: {space,a,b} } }`.
 * Hang keys are per CATEGORY since ADR 0057 (`hang:short`); docs saved before
 * carry per-TYPE keys (`hang:DH`), which pass the same shape check here and
 * are re-keyed at hydrate by migrateStorageNeedKeys() — this layer stays
 * rules-blind, so it must not (and does not) filter them out.
 * Every figure is re-validated on the way in and out: a non-finite or negative
 * number becomes null ("not stated"), which is NOT the same as 0 ("wants none")
 * — the fallback of `needed` to `existing` depends on telling those apart.
 */
function cleanStorageNeeds(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const num = (v) => (Number.isFinite(v) && v >= 0 ? v : null);
  const slots = (o) => ({ space: num(o?.space), a: num(o?.a), b: num(o?.b) });
  const out = {};
  for (const [key, entry] of Object.entries(raw)) {
    if (!/^(hang|shoes|drawers):[A-Za-z0-9_-]+$/.test(key) || !entry || typeof entry !== 'object') continue;
    const existing = slots(entry.existing);
    const needed = slots(entry.needed);
    const shoeGroups = key.startsWith('shoes:') && Array.isArray(entry.shoeGroups)
      ? entry.shoeGroups.flatMap((group) => {
        const item = {
          id: typeof group?.id === 'string' ? group.id.slice(0, 80) : '',
          label: typeof group?.label === 'string' ? group.label.trim().slice(0, 80) : '',
          slot: ['a', 'b'].includes(group?.slot) ? group.slot : 'space',
          existingPairs: Number.isInteger(group?.existingPairs) && group.existingPairs >= 0 ? group.existingPairs : null,
          neededPairs: Number.isInteger(group?.neededPairs) && group.neededPairs >= 0 ? group.neededPairs : null,
          widthUnits: Number.isFinite(group?.widthUnits) && group.widthUnits > 0 ? group.widthUnits : null,
          lengthUnits: Number.isFinite(group?.lengthUnits) && group.lengthUnits > 0 ? group.lengthUnits : null,
        };
        return item.id && item.label ? [item] : [];
      }) : [];
    const stated = [...Object.values(existing), ...Object.values(needed)].some((v) => v !== null) || shoeGroups.length > 0;
    if (stated) out[key] = { existing, needed, ...(key.startsWith('shoes:') ? { shoeGroups } : {}) };
  }
  return out;
}

/** Durable provenance for client-supplied Storage needs. */
function cleanStorageNeedsSource(raw) {
  if (!raw || raw.kind !== 'client') return null;
  const required = ['requestId', 'sourceSketchId', 'submittedAt', 'importedAt'];
  if (!required.every((key) => typeof raw[key] === 'string' && raw[key].trim())) return null;
  return {
    kind: 'client', requestId: raw.requestId.trim(), sourceSketchId: raw.sourceSketchId.trim(),
    submittedAt: raw.submittedAt, importedAt: raw.importedAt,
    designerEditedAt: typeof raw.designerEditedAt === 'string' && raw.designerEditedAt.trim()
      ? raw.designerEditedAt : null,
    opportunity: raw.opportunity && typeof raw.opportunity.client === 'string' && typeof raw.opportunity.opportunity === 'string'
      ? { client: raw.opportunity.client.trim().slice(0, 160), opportunity: raw.opportunity.opportunity.trim().slice(0, 160) } : null,
    importedSpaces: (Array.isArray(raw.importedSpaces) ? raw.importedSpaces : []).slice(0, 30).flatMap((space) => {
      const item = {
        id: typeof space?.id === 'string' ? space.id.slice(0, 80) : '',
        name: typeof space?.name === 'string' ? space.name.trim().slice(0, 120) : '',
        mapping: ['space', 'a', 'b', 'ab', 'ba'].includes(space?.mapping) ? space.mapping : 'space',
        notes: typeof space?.notes === 'string' ? space.notes.trim().slice(0, 2000) : '',
      };
      return item.id && item.name ? [item] : [];
    }),
  };
}

/**
 * Space pricing (ADR 0058), rebuilt field by field. RULES-BLIND like every
 * helper here: the level is any whole number ≥ 1 (the UI offers only the
 * levels the multiplier rule defines; an out-of-range stored level prices at
 * factor 1 rather than being silently reset), the countertop material any
 * non-empty string (validated against the countertop rule at price time —
 * an unknown code shows a visible needs-pricing line). Totals are the last
 * computed stamp; null = never computed, never coerced to 0.
 */
function cleanPricing(raw) {
  const money = (v) => (Number.isFinite(v) && v >= 0 ? v : null);
  return {
    level: Number.isInteger(raw?.level) && raw.level >= 1 ? raw.level : 1,
    countertopMaterial: typeof raw?.countertopMaterial === 'string' && raw.countertopMaterial.trim()
      ? raw.countertopMaterial.trim() : null,
    low: money(raw?.low),
    high: money(raw?.high),
    needsPricing: Number.isInteger(raw?.needsPricing) && raw.needsPricing >= 0 ? raw.needsPricing : 0,
  };
}

/**
 * Basic mode (ADR 0059), rebuilt field by field, RULES-BLIND like every
 * helper here: the space type is any non-empty string (the UI offers only
 * the basic_space_types keys; an unknown stored key simply stops filtering),
 * bucket keys are any non-empty string (a card whose bucket definition
 * vanished shows a visible needs-pricing line, never silently drops), and
 * the bounds must be four positive numbers or they fall back to the rule
 * default as a set. Cards keep their ids (unplace bookkeeping) and drop at
 * qty 0. `validHosts` (hydrate only) re-checks a card's wall ASSIGNMENT —
 * a vanished wall unassigns the card rather than deleting its units.
 */
function cleanBasic(raw, validHosts = null) {
  const boundsOf = (b) => {
    const nums = ['lowHeightIn', 'lowDepthIn', 'highHeightIn', 'highDepthIn']
      .map((k) => (Number.isFinite(b?.[k]) && b[k] > 0 ? b[k] : null));
    if (nums.some((v) => v === null)) return null;
    const [lh, ld, hh, hd] = nums;
    if (lh > hh || ld > hd) return null;
    return { lowHeightIn: lh, lowDepthIn: ld, highHeightIn: hh, highDepthIn: hd };
  };
  const cards = (Array.isArray(raw?.cards) ? raw.cards : [])
    .filter((c) => c && typeof c.id === 'string' && c.id
      && typeof c.bucketKey === 'string' && c.bucketKey.trim()
      && Number.isInteger(c.qty) && c.qty > 0)
    .map((c) => ({
      id: c.id,
      bucketKey: c.bucketKey.trim(),
      qty: c.qty,
      // Drawer ADDER count, PER UNIT (owner). Absent/invalid means none, so a
      // card stored before the adder existed reads back as 0 drawers and
      // prices exactly as it did — no migration.
      drawers: Number.isInteger(c.drawers) && c.drawers > 0 ? c.drawers : 0,
      wallId: typeof c.wallId === 'string' && c.wallId && (!validHosts || validHosts.has(c.wallId))
        ? c.wallId : null,
    }));
  return {
    mode: raw?.mode === 'basic' ? 'basic' : 'detailed',
    spaceType: typeof raw?.spaceType === 'string' && raw.spaceType.trim() ? raw.spaceType.trim() : null,
    bounds: boundsOf(raw?.bounds),
    cards,
  };
}

/**
 * Corner ceiling heights (ADR 0044), `{ [segmentId]: heightUnits }`. Only
 * positive finite numbers survive: a corner that cannot be read has no height,
 * which resolves to the room's ceiling — never to zero, which would draw a wall
 * with no top at all.
 */
function cleanCornerHeights(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [id, value] of Object.entries(raw)) {
    if (typeof id === 'string' && id && Number.isFinite(value) && value > 0) out[id] = value;
  }
  return out;
}

/**
 * Interior height breakpoints, `{ [wallId]: [{atUnits, heightUnits}] }`. A
 * breakpoint at or before the wall's start is dropped here rather than clamped —
 * the wall's length is not known at this layer, and the profile builder drops
 * out-of-range points again with the length in hand.
 */
function cleanWallHeightBreaks(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [id, list] of Object.entries(raw)) {
    if (typeof id !== 'string' || !id || !Array.isArray(list)) continue;
    const points = list
      .filter((b) => b && Number.isFinite(b.atUnits) && b.atUnits > 0 && Number.isFinite(b.heightUnits) && b.heightUnits > 0)
      .map((b) => ({ atUnits: b.atUnits, heightUnits: b.heightUnits }));
    if (points.length) out[id] = points;
  }
  return out;
}

/**
 * A text note's size, in INCHES.
 *
 * Sizes were briefly stored as fixed drawing pixels (14/22/34/52) before being
 * recalibrated to real inches against what is already on the plan — the wall
 * measurements and the section codes (owner, 2026-07-29). Notes written in that
 * window would read as 14–52 INCH lettering, so they are mapped back onto the
 * new ladder here rather than left to swamp a drawing.
 *
 * The threshold is safe because the two ranges cannot overlap: the inch ladder
 * tops out at 5.1" and the pixel ladder started at 14.
 */
const LEGACY_PX_TO_INCHES = new Map([[14, 1.8], [22, 2.9], [34, 4], [52, 5.1]]);
const LEGACY_PX_THRESHOLD = 8;

function textSizeInches(value) {
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < LEGACY_PX_THRESHOLD) return value; // already inches
  if (LEGACY_PX_TO_INCHES.has(value)) return LEGACY_PX_TO_INCHES.get(value);
  // A pixel size the app never offered (hand-edited data): fall to the nearest
  // rung of the old ladder rather than inventing a size.
  let nearest = 22;
  for (const px of LEGACY_PX_TO_INCHES.keys()) {
    if (Math.abs(px - value) < Math.abs(nearest - value)) nearest = px;
  }
  return LEGACY_PX_TO_INCHES.get(nearest);
}
